import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AnnaAvatar } from '../avatar/AnnaAvatar';
import { CaptionArea } from './CaptionArea';
import { ProposalFocus } from './ProposalFocus';
import { StageCardView } from './StageCardView';
import { AlarmCard } from './cards/AlarmCard';
import { useMotionPreference } from '../../hooks/useMotionPreference';
import type { WallGeometry } from '../../utils/orbitLayout';
import type { AnnaStage } from './useAnnaStage';
interface StackSceneProps {
  stage: AnnaStage;
  g: WallGeometry;
  nothingGoingOn: boolean;
}
const EASE = [0.23, 1, 0.32, 1] as const;

/** Narrow screens: no room to orbit, so Anna sits on top and her cards stack beneath her. */
export function StackScene({
  stage,
  g,
  nothingGoingOn
}: StackSceneProps) {
  const reduce = useMotionPreference();
  const size = Math.min(g.w * 0.86, g.h * (stage.view === 'approvals' ? 0.26 : 0.48));
  return <div className="no-scrollbar absolute inset-0 z-[11] overflow-y-auto px-4 pb-48 pt-36" data-id="element-1709" data-mp="350hbxpsophtd">
      <div className="mx-auto flex max-w-[620px] flex-col items-center gap-6" data-id="element-1710" data-mp="11uoleq3t6bq4">
        <div className="pointer-events-none shrink-0" style={{
        width: size,
        height: size
      }} aria-hidden data-id="element-1711" data-mp="xu4onnn5da4i">
          <AnnaAvatar state={stage.avatarState} pulseKey={stage.pulseKey} data-id="element-1712" data-mp="t2mmwxdav5ih" />
        </div>
        {stage.view !== 'sleep' && <div className="w-full text-center" data-id="element-1713" data-mp="o4bbwjb9qoyo">
            <CaptionArea stage={stage} nothingGoingOn={nothingGoingOn} compact={stage.view === 'approvals' || stage.quiet} data-id="element-1714" data-mp="j602g9ovyggz" />
          </div>}
        <AnimatePresence data-id="element-1715" data-mp="e52lu1jixuhl">{stage.view === 'alarm' && <AlarmCard key="alarm" onAck={stage.ackAlarm} data-id="element-1716" data-mp="a4inwvr4mmue" />}</AnimatePresence>
        {stage.view === 'approvals' ? <ProposalFocus pending={stage.pending} outcomes={stage.outcomes} onApprove={stage.approve} onReject={stage.reject} onEdit={() => stage.showToast('Aanpassen kan op je bureauscherm of telefoon.', 'meldt')} fromY={-size} compact data-id="element-1717" data-mp="56h7yjozz3n5" /> : stage.view === 'sleep' ? null : <div className="flex w-full flex-col gap-5" data-id="element-1718" data-mp="2netma5azehs3">
            <AnimatePresence initial={false} data-id="element-1719" data-mp="2ngmxlxwjhq6f">
              {stage.cards.map(c => <motion.div key={c.uid} layout={!reduce} initial={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            y: -24,
            scale: 0.96
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            y: -24,
            scale: 0.96
          }} transition={{
            duration: 0.25,
            ease: EASE
          }} data-id="element-1720" data-mp="29oymnxy117r1">
                  <StageCardView card={c} onRemove={stage.removeCard} onApproved={stage.notifyApproved} onSound={stage.sound} data-id="element-1721" data-mp="2sdn4gb17qwyl" />
                </motion.div>)}
            </AnimatePresence>
          </div>}
      </div>
    </div>;
}